// Invoices (was "Billing periods", SCR-006): every customer's month for one UTC month; a row opens
// its summary with View invoice and Close month (`billing:write` or super-admin — `session.can` already
// treats a super-admin as holding every permission, ADR-031). The ledger total here is AWS list
// cost; what the customer pays is on the invoice itself (the Customers area's invoice route).
import { useContext, useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { SessionContext, type Me } from "../../session/session";
import { customerName } from "../../ui/customers";
import { useCustomerDirectory } from "../../ui/customer-directory";
import { LabelText } from "@nexus/ui";
import { month as monthRule } from "@nexus/ui";
import { actorDisplay, actorTitle, knownActor } from "../../ui/identity";
import { MonthPicker } from "@nexus/ui";
import { addMonths, currentMonth as currentUtcMonth, longMonthLabel, monthLabel } from "@nexus/ui";
import { Chip, EmptyState, FilterBar, NameCell, PageHeader, Segmented } from "@nexus/ui";
import { Link } from "../../ui/shell";
import { RowDetail } from "@nexus/ui";
import { DataTable, Money, type Column } from "@nexus/ui";
import { CloseDialog } from "./close-dialog";
import {
  CLOSE_DIALOG_CLOSED,
  STATUS_OPTIONS,
  canClosePeriod,
  closeFailed,
  closeRequestBody,
  compareNullable,
  comparePeriodsByName,
  customerBillingHref,
  defaultFilters,
  formatDay,
  invoiceHref,
  notFullyMetered,
  openCloseDialog,
  periodStatusView,
  periodsQuery,
  setForce,
  setReason,
  startClosing,
  type BillingPeriod,
  type CloseDialogState,
  type PeriodsFilters,
} from "./model";

interface FetchState {
  loading: boolean;
  rows: BillingPeriod[];
  error: ApiError | null;
}

function PeriodStatus({ period, now }: { period: BillingPeriod; now: Date }) {
  const view = periodStatusView(period, now);
  return <Chip tone={view.tone}>{view.label}</Chip>;
}

function ClosedBy({ period, me }: { period: BillingPeriod; me: Me | undefined }) {
  if (period.closedAt === null) return <span class="muted">—</span>;
  return (
    <span class="stack-cell">
      <span>{formatDay(period.closedAt)}</span>
      <span class="cell-sub" title={actorTitle(period.closedBy, knownActor(period, "closedBy"))}>
        by {actorDisplay(period.closedBy, me, knownActor(period, "closedBy"))}
        {period.forced ? " · forced" : ""}
      </span>
      {period.forced && period.forceReason && <span class="cell-sub invoices-reason">“{period.forceReason}”</span>}
    </span>
  );
}

function count(value: number | null) {
  return value === null ? <span class="muted">—</span> : value.toLocaleString("en-US");
}

export function Periods() {
  const client = useApi();
  const services = useContext(SessionContext);
  const session = services !== null && services.state.status === "signed-in" ? services.state.session : null;
  const canWrite = session?.can("billing:write") ?? false;
  const { entries } = useCustomerDirectory();
  const nameOf = (orgId: string) => customerName(entries, orgId);

  const [filters, setFilters] = useState<PeriodsFilters>(() => defaultFilters(new Date()));
  const [state, setState] = useState<FetchState>({ loading: true, rows: [], error: null });
  const [reloadToken, setReloadToken] = useState(0);
  const [dialog, setDialog] = useState<CloseDialogState>(CLOSE_DIALOG_CLOSED);
  const [selected, setSelected] = useState<BillingPeriod | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (monthRule(filters.period) !== null) {
      setState({ loading: false, rows: [], error: null });
      return;
    }
    setState((current) => ({ ...current, loading: true, error: null }));
    client
      .get("/v1/admin/billing-periods", { query: periodsQuery(filters) })
      .then((page) => {
        if (!cancelled) setState({ loading: false, rows: page.items, error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError) setState({ loading: false, rows: [], error });
        else throw error;
      });
    return () => {
      cancelled = true;
    };
  }, [filters, reloadToken, client]);

  const reload = () => setReloadToken((token) => token + 1);

  const confirmClose = async () => {
    setDialog(startClosing(dialog));
    try {
      await client.post("/v1/admin/orgs/{orgId}/billing-periods/{period}/close", {
        path: { orgId: dialog.orgId, period: dialog.period },
        body: closeRequestBody(dialog),
      });
      setDialog(CLOSE_DIALOG_CLOSED);
      reload();
    } catch (error) {
      if (error instanceof ApiError) setDialog((current) => closeFailed(current, error));
      else throw error;
    }
  };

  const now = new Date();
  const dialogPeriod = state.rows.find((p) => p.orgId === dialog.orgId && p.period === dialog.period) ?? null;
  const monthName = longMonthLabel(filters.period);

  const columns: Column<BillingPeriod>[] = [
    {
      key: "customer",
      header: "Customer",
      cell: (p) => <NameCell name={nameOf(p.orgId)} id={p.orgId} href={customerBillingHref(p.orgId)} />,
      compare: comparePeriodsByName(nameOf),
    },
    { key: "month", header: "Month", cell: (p) => monthLabel(p.period) },
    {
      key: "status",
      header: "Status",
      help: "invoices.status",
      cell: (p) => <PeriodStatus period={p} now={now} />,
      compare: (a, b) => a.status.localeCompare(b.status),
    },
    {
      key: "total",
      header: "Ledger total",
      help: "invoices.ledgerTotal",
      numeric: true,
      cell: (p) => (p.totalMicroUsd === null ? <span class="muted">—</span> : <Money micro={p.totalMicroUsd} />),
      compare: (a, b) => compareNullable(a.totalMicroUsd, b.totalMicroUsd),
    },
    {
      key: "requests",
      header: "Requests",
      help: "invoices.requests",
      numeric: true,
      cell: (p) => count(p.requests),
      compare: (a, b) => compareNullable(a.requests, b.requests),
    },
    {
      key: "unmetered",
      header: "Not fully metered",
      help: "invoices.unmetered",
      numeric: true,
      cell: (p) => count(notFullyMetered(p)),
      compare: (a, b) => compareNullable(notFullyMetered(a), notFullyMetered(b)),
    },
    { key: "closed", header: "Closed", help: "invoices.closed", cell: (p) => <ClosedBy period={p} me={session?.me} /> },
  ];

  const loaded = !state.loading && state.error === null;
  const empty = loaded && state.rows.length === 0;

  return (
    <div class="page">
      <PageHeader
        title="Invoices"
        lede="Every customer's invoice for one month. Open months are live previews; closing a month that has ended prices its usage and freezes the invoice for good."
      />
      <FilterBar end={loaded && state.rows.length > 0 ? <span class="muted">{state.rows.length.toLocaleString("en-US")} customers</span> : undefined}>
        <div class="filter">
          <LabelText id="invoices-month-label" label="Month" help="invoices.month" compact />
          <MonthPicker
            id="invoices-month"
            labelledBy="invoices-month-label"
            value={filters.period}
            max={currentUtcMonth(now)}
            onChange={(period) => setFilters({ ...filters, period })}
          />
        </div>
        <div class="filter">
          <LabelText id="invoices-status-label" label="Status" help="invoices.status" compact />
          <Segmented
            options={STATUS_OPTIONS}
            value={filters.status}
            labelledBy="invoices-status-label"
            onChange={(status) => setFilters({ ...filters, status })}
          />
        </div>
      </FilterBar>

      {empty ? (
        <EmptyState
          title={filters.status === "" ? `No invoices for ${monthName} yet` : `No ${filters.status} invoices for ${monthName}`}
          lede="Each customer has one invoice per UTC month. Nexus opens the month for every customer on its own, so this list fills itself; once a month has ended, close it here to make its invoice final."
          steps={[
            { title: "Pick a month", text: "Use the month picker above; last month is usually the one to close." },
            { title: "Check the invoice", text: "View invoice shows the lines, the customer's price and anything that would block the close." },
            { title: "Close the month", text: "Close month freezes the invoice. A month with unverified or missing model prices needs a reason." },
          ]}
          actions={
            <>
              <button
                type="button"
                class="button button-primary"
                onClick={() => setFilters({ ...filters, period: addMonths(currentUtcMonth(now), -1) })}
              >
                Show last month
              </button>
              {filters.status !== "" && (
                <button type="button" class="button" onClick={() => setFilters({ ...filters, status: "" })}>
                  Show every status
                </button>
              )}
            </>
          }
        />
      ) : (
        <div class="card table-card">
          <DataTable
            caption={`Invoices, ${monthName}`}
            hideCaption
            columns={columns}
            rows={state.rows}
            rowKey={(p) => `${p.orgId}@${p.period}`}
            loading={state.loading}
            error={state.error}
            onRetry={reload}
            emptyMessage={`No invoices for ${monthName}.`}
            onRowClick={setSelected}
            rowLabel={(p) => `${nameOf(p.orgId)}, ${monthLabel(p.period)}`}
          />
        </div>
      )}
      {selected && (
        <RowDetail
          title={`${nameOf(selected.orgId)} · ${longMonthLabel(selected.period)}`}
          open
          onClose={() => setSelected(null)}
          facts={[
            { label: "Status", value: <PeriodStatus period={selected} now={now} /> },
            { label: "Ledger total", value: selected.totalMicroUsd === null ? "—" : <Money micro={selected.totalMicroUsd} /> },
            { label: "Requests", value: count(selected.requests) },
            { label: "Not fully metered", value: count(notFullyMetered(selected)) },
            { label: "Closed", value: <ClosedBy period={selected} me={session?.me} /> },
          ]}
          actions={
            <>
              <Link href={invoiceHref(selected)} class="button button-secondary">
                View invoice
              </Link>
              {canWrite && canClosePeriod(selected) && (
                <button
                  type="button"
                  class="button button-primary"
                  onClick={() => {
                    setSelected(null);
                    setDialog(openCloseDialog(selected.orgId, selected.period));
                  }}
                >
                  Close month
                </button>
              )}
            </>
          }
        />
      )}
      <CloseDialog
        state={dialog}
        period={dialogPeriod}
        onForce={(force) => setDialog(setForce(dialog, force))}
        onReason={(reason) => setDialog(setReason(dialog, reason))}
        onConfirm={() => void confirmClose()}
        onClose={() => setDialog(CLOSE_DIALOG_CLOSED)}
      />
    </div>
  );
}
